'use client';

import React from 'react';
import { ClientCard } from './GameBoard';
import CardBack from './CardBack';
import AdversaryCardFace from './AdversaryCardFace';

type Props = {
  card: ClientCard;
  onClick: () => void;
  disabled: boolean;
  shuffleOffset?: { x: number; y: number; rotate: number };
  isShuffling?: boolean;
};

export default function GameCard({ card, onClick, disabled, shuffleOffset, isShuffling }: Props) {
  const isFlipped = card.state === 'flipped' || card.state === 'matched';
  const isMatched = card.state === 'matched';

  const shuffleTransform = shuffleOffset
    ? `translate(${shuffleOffset.x}px, ${shuffleOffset.y}px) rotate(${shuffleOffset.rotate}deg)`
    : 'translate(0px, 0px) rotate(0deg)';

  return (
    <div
      className={`aspect-square w-full select-none relative
        ${disabled || isMatched ? 'cursor-default' : 'cursor-pointer'}`}
      style={{
        perspective: '1000px',
        transform: shuffleTransform,
        transition: isShuffling
          ? 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
          : 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        zIndex: shuffleOffset && (shuffleOffset.x !== 0 || shuffleOffset.y !== 0) ? 10 : 1,
      }}
      onClick={() => {
        if (!disabled && card.state === 'hidden') onClick();
      }}
      role="button"
      tabIndex={disabled || isMatched ? -1 : 0}
      aria-label={isFlipped ? `${card.name} — ${card.origin}` : 'Hidden adversary card'}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled && card.state === 'hidden') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div
        className="relative w-full h-full card-flip-transition"
        style={{
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* Back face */}
        <div
          className="absolute inset-0"
          style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
        >
          <CardBack />
        </div>

        {/* Front face */}
        <div
          className="absolute inset-0"
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
        >
          <AdversaryCardFace
            adversary={{
              id: card.adversaryId,
              name: card.name,
              animalType: card.animalType,
              origin: card.origin as any,
              apt: card.apt ?? undefined,
              description: '',
              primaryColor: card.primaryColor,
              accentColor: card.accentColor,
              emoji: card.emoji,
              image: card.image ?? undefined,
            }}
            isMatched={isMatched}
          />
        </div>
      </div>

      {/* Matched glow */}
      {isMatched && (
        <div className="absolute inset-0 rounded-xl glow-green pointer-events-none" />
      )}
    </div>
  );
}